import React, { useState, useMemo, useRef } from "react";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import 'ag-grid-enterprise';
import ConfirmDeleteModal from "../confirmdeletemodal";
import api from '../../../config/axiosConfig'

const ItemTypes = () => { // To store Firestore data
    const [formData, setFormData] = useState({  
        name: '',
        isactive: true // Default to true
    });
    const [isEditMode, setIsEditMode] = useState(false); // To track if we are in edit mode
    const [showModal, setShowModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const gridRef = useRef();

    
    const columnDefs = [
        { headerName: 'ID', field: 'id', sort: "desc", filter: 'agNumberColumnFilter' },
        { headerName: 'Name', field: 'name', filter: 'agTextColumnFilter' },
        { headerName: 'Is Active', field: 'isactive' },
        { headerName: 'Created At', field: 'createddate', filter: 'agDateColumnFilter' },
        { headerName: 'Updated At', field: 'modifieddate', filter: 'agDateColumnFilter' },
        {
            field: "actions",
            headerName: "Actions",
            cellRenderer: (params) => (
                <Button
                    variant="danger"
                    className="btn-sm"
                    onClick={() => handleDelete(params.data)}
                >
                    <i class="bi bi-trash3"></i>
                </Button>
            ),
        },
        // Add more columns as needed
    ];

    const sideBar = useMemo(() => {
        return {
            toolPanels: [
                {
                    id: 'columns',
                    labelDefault: 'Columns',
                    labelKey: 'columns',
                    iconKey: 'columns',
                    toolPanel: 'agColumnsToolPanel',
                },
                {
                    id: 'filters',
                    labelDefault: 'Filters',
                    labelKey: 'filters',
                    iconKey: 'filter',
                    toolPanel: 'agFiltersToolPanel',
                }
            ],
            defaultToolPanel: '',
        };
    }, []);

       // Server-Side Datasource
       const datasource = {
        getRows: async function (params) {
            try {
                const { request } = params;
                const response = await api.post('/itemTypes/ag',{ ...request } );

                const { rows, lastRow } = response.data;

                // Send data back to AG Grid
                params.successCallback(rows, lastRow);
            } catch (error) {
                console.error("Error fetching rows:", error);
                params.failCallback();
            }
        }
    };

    const gridOptions = {
        rowModelType: 'serverSide',
        columnDefs: columnDefs,

        defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            floatingFilter: true,
            enableValue: true,
            enableRowGroup: true,
            enablePivot: true,
        },

        sideBar: sideBar,
        rowSelection: 'single',
        columnHoverHighlight: true,
        suppressRowGroupHidesColumns: true,
        cacheBlockSize: 20,

    }

 

    // Attach the datasource when the grid is ready
    const onGridReady = (params) => {
        params.api.setServerSideDatasource(datasource);
    };

    // Row selection handler
    const onRowSelected = (event) => {
        if (event.node.isSelected()) {
            const selectedData = event.data;
            setFormData({
                id: selectedData.id,
                name: selectedData.name,
                isactive: selectedData.isactive
            });
            setIsEditMode(true);  // Switch to edit mode
        }
    };

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value // Handle checkbox separately
        }));
    };

    // Handle form submission
    const handleAddData = async (e) => {
        e.preventDefault();
    
        if (formData.name) {
            try {
                const url = isEditMode ? `/itemTypes/${formData.id}` : '/itemTypes';
                const method = isEditMode ? 'put' : 'post';
                
                // Make the Axios call using the api instance
                await api({
                    method,
                    url,
                    data: {
                        name: formData.name,
                        isactive: formData.isactive,
                    },
                });
    
                // Reset form and fetch updated data
                setFormData({ id: null, name: '', isactive: true });
                setIsEditMode(false);
               
                // Refresh the grid's data source
                gridRef.current.api.refreshServerSideStore({ purge: true });

            } catch (error) {
                console.error('Error:', error);
            }
        } else {
            alert('Please fill out the name field.');
        }
    };
    
    

    // Reset form for new entry
    const handleNewEntry = () => {
        setFormData({ id: null, name: '', isactive: true });
        setIsEditMode(false);  // Switch to add mode
    };

    const handleDelete = (category) => {
        setSelectedRow(category); // Store the category to be deleted
        setShowModal(true); // Show the modal
    };

    const confirmDelete = async () => {
        if (selectedRow) {
            try {
                await api.delete(`/itemTypes/${selectedRow.id}`);
                setShowModal(false);
                // Refresh the grid's data source
                gridRef.current.api.refreshServerSideStore({ purge: true });

            } catch (error) {
                console.error('Error deleting item:', error);
            }
        }
    };

    return (
        <>

            <Container fluid>
                <Row className="mt-4">
                    <h6 className="text-Start">Category List</h6>
                    <div className="ag-theme-alpine" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            ref={gridRef}
                            gridOptions={gridOptions}
                            onGridReady={onGridReady}
                            onRowSelected={onRowSelected}
                        />
                    </div>
                </Row>
                <Row className="mt-4 border shadow p-4">
                    <Col md={12}>
                        <h6 className="text-start">Add Category</h6>
                        <Form onSubmit={handleAddData}>
                            <Form.Group className="mb-3 d-flex align-items-center" controlId="formBasicName">
                                <Form.Label className="me-3">Name:</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    placeholder="Enter category name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    className="me-3"
                                    style={{ width: '50%' }}
                                />
                                <Form.Check
                                    type="checkbox"
                                    name="isactive"
                                    label="Is Active"
                                    checked={formData.isactive}
                                    onChange={handleChange}
                                    className="ms-4"
                                />
                            </Form.Group>
                            <Button variant="primary" type="submit">
                                {isEditMode ? "Update" : "Add"}
                            </Button>
                            {isEditMode ?
                                <Button variant="secondary" className="ms-3" onClick={handleNewEntry}>
                                    Create New Entry
                                </Button>
                                : ''
                            }
                        </Form>
                    </Col>
                </Row>
            </Container>

            {/* Modal for confirming deletion */}
            <ConfirmDeleteModal
                show={showModal}
                handleClose={() => setShowModal(false)}
                handleConfirm={confirmDelete}
                title="Confirm Deletion"
                body={`Are you sure you want to delete ${selectedRow?.name}?`}
                confirmText="Delete"
            />

        </>
    );
};

export default ItemTypes;
